import {
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  KeyboardIcon,
  Tooltip,
  TooltipPositionsEnum,
  Typography,
} from '@acceso/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nextTranslationKeys } from '@/i18n/i18next';

type TShorcut = {
  key: string;
  description: I18nextTranslationKeys;
};

const GENERALES_HOTKEYS: TShorcut[] = [
  {
    key: 'Ctrl + I',
    description: 'Añade un nuevo item',
  },
  {
    key: 'Ctrl + F12',
    description: 'Reportar incidencia',
  },
  {
    key: 'Arriba',
    description: 'Scroll hacia arriba',
  },
  {
    key: 'Abajo',
    description: 'Scroll hacia abajo',
  },
  {
    key: 'Alt + Arriba',
    description: 'Seleccionar el item superior del listado',
  },
  {
    key: 'Alt + Abajo',
    description: 'Seleccionar el item inferior del listado',
  },
  {
    key: 'Ctrl + inicio',
    description: 'Primer item del listado',
  },
  {
    key: 'Ctrl + fin',
    description: 'Último item del listado',
  },
];
const PLAYER_HOTKEYS: TShorcut[] = [
  {
    key: 'Ctrl + arriba',
    description: 'Reproducir',
  },
  {
    key: 'Ctrl + abajo',
    description: 'Pausar',
  },
  {
    key: 'Ctrl + izquierda',
    description: 'Rebobinar contenido multimedia',
  },
  {
    key: 'Ctrl + Derecha',
    description: 'Avanzar contenido multimedia',
  },
  {
    key: 'Derecha',
    description: 'Reproducción rápida (foco en el vídeo)',
  },
  {
    key: 'Izquierda',
    description: 'Reproducción en reversa (foco en el vídeo)',
  },
  {
    key: 'Shift + derecha',
    description: 'Avanzar 5 segundos (foco en la barra de progreso)',
  },
  {
    key: 'Shift + izquierda',
    description: 'Retroceder 5 segundos (foco en la barra de progreso)',
  },
  {
    key: 'Ctrl + shift + "+"',
    description: 'Subir volumen',
  },
  {
    key: 'Ctrl + shift + "-"',
    description: 'Bajar volumen',
  },
  {
    key: 'Ctrl + F10',
    description: 'Ver en pantalla completa',
  },
  {
    key: 'Inicio',
    description: 'Ir al inicio del reproductor',
  },
  {
    key: 'Fin',
    description: 'Ir al final del reproductor',
  },
  {
    key: 'Punto (".")',
    description: 'Avanza frame a frame',
  },
  {
    key: 'Coma (",")',
    description: 'Rebobina frame a frame',
  },
];
/* const SEGMENT_HOTKEYS: TShorcut[] = []; */
const CUT_HOTKEYS: TShorcut[] = [
  {
    key: 'F2',
    description: 'Iniciar corte',
  },
  {
    key: 'F9',
    description: 'Finalizar corte',
  },
  {
    key: 'F8',
    description: 'Borrar la marca de fin del corte',
  },
  {
    key: 'F4',
    description: 'Borrar descripción',
  },
  {
    key: 'Ctrl + C',
    description: 'Copiar texto',
  },
  {
    key: 'Ctrl + V',
    description: 'Pegar texto',
  },
  {
    key: 'Ctrl + alt + C',
    description: 'Abrir listado de cortes',
  },
  {
    key: 'Ctrl + D',
    description: 'Crear declaración',
  },
  {
    key: 'Ctrl + P',
    description: 'Crear persona de interés',
  },
  {
    key: 'Ctrl + L',
    description: 'Crear logotipo',
  },
];

const TooltipText = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 p-2" data-id="shortcut-tooltip">
      <div className="rounded-md bg-yellow-500/20 p-2 text-sm text-yellow-100">
        <Typography>
          ⚠️{' '}
          {t(
            `Algunas teclas como F2, F4, F10, etc. pueden no funcionar si tu teclado tiene funciones multimedia por defecto (como brillo o volumen). Si estás en Mac o en un portátil, prueba pulsando fn + F* (por ejemplo, fn + F2). También puedes cambiar el comportamiento del teclado en los ajustes del sistema`
          )}
        </Typography>
      </div>
      <div className="flex items-center gap-2">
        <KeyboardIcon size={IconSizeEnum['2XL']} className="cursor-pointer" />
        <div className="flex flex-col gap-1">
          <Typography>{t('Atajos de teclado para revisión')}</Typography>
          <Typography>
            {t(
              'Atajos de teclado usados en la plataforma de revisión y ordenados por funcionalidad'
            )}
          </Typography>
        </div>
      </div>
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="flex flex-col gap-2">
          <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Semibold}>
            {t('Generales')}
          </Typography>
          <hr></hr>
          {GENERALES_HOTKEYS.map((hotkey, index) => (
            <div key={hotkey.key} className="flex flex-col gap-2">
              <div className="flex gap-2 pl-2 text-[#CCD6DD]">
                <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Bold}>
                  {hotkey.key}:
                </Typography>
                <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Regular}>
                  {t(hotkey.description)}
                </Typography>
              </div>
              {index < GENERALES_HOTKEYS.length - 1 && <hr />}
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Semibold}>
            {t('Repdroductor de vídeo')}
          </Typography>
          <hr></hr>
          {PLAYER_HOTKEYS.map((hotkey, index) => (
            <div key={hotkey.key} className="flex flex-col gap-2">
              <div className="flex gap-2 pl-2 text-[#CCD6DD]">
                <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Bold}>
                  {hotkey.key}:
                </Typography>
                <Typography fontSize={FontSizeEnum.Basic}>{t(hotkey.description)}</Typography>
              </div>
              {index < PLAYER_HOTKEYS.length - 1 && <hr />}
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Semibold}>
            {t('Cortes')}
          </Typography>
          <hr></hr>
          {CUT_HOTKEYS.map((hotkey, index) => (
            <div key={hotkey.key} className="flex flex-col gap-2">
              <div className="flex gap-2 pl-2 text-[#CCD6DD]">
                <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Bold}>
                  {hotkey.key}:
                </Typography>
                <Typography fontSize={FontSizeEnum.Basic}>{t(hotkey.description)}</Typography>
              </div>
              {index < CUT_HOTKEYS.length - 1 && <hr />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
export function Shorcut() {
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  return (
    <div className="relative">
      <div onClick={() => setOpen(prev => !prev)} className="cursor-pointer" id="shorcut">
        <Tooltip help={t('Atajos de teclado')} position={TooltipPositionsEnum.LEFT} grow>
          <KeyboardIcon size={IconSizeEnum.SM} />
        </Tooltip>
      </div>
      {open && (
        <div className="absolute right-0 z-[100] mt-2 max-h-[800px] w-[calc(100vw-80px)] overflow-auto rounded-lg bg-accent-active-primary-40 p-4 text-white shadow-lg lg:w-[975px]">
          <TooltipText />
        </div>
      )}
    </div>
  );
}
